import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';

export interface InsightItem {
  itemId: string;
  name: string;
  giro30d: number;
  giroLabel: 'alto' | 'medio' | 'baixo';
  vezesZerou: number;
  minAtual: number;
  minSugerido: number | null;
}

export interface InventoryOrder {
  id: string;
  itemId: string;
  itemName: string;
  quantityOrdered: number;
  supplierName: string;
  isPartner: boolean;
  partnerCampaignId: string | null;
  status: 'pending' | 'ordered' | 'received' | 'cancelled';
  notes: string | null;
  orderedAt: string | null;
  receivedAt: string | null;
  createdAt: string;
}

export interface Supplier {
  name: string;
  isPartner: boolean;
  tier: string | null;
  campaignId: string | null;
}

export function useInventoryInsights() {
  const queryClient = useQueryClient();

  const insightsQuery = useQuery<{ insights: InsightItem[] }>({
    queryKey: ['inventory-insights'],
    queryFn: async () => {
      const res = await api.get('/inventory/insights');
      return res.data;
    },
    staleTime: 60 * 60 * 1000, // 1h cache
  });

  const ordersQuery = useQuery<{ orders: InventoryOrder[] }>({
    queryKey: ['inventory-orders'],
    queryFn: async () => {
      const res = await api.get('/inventory/orders');
      return res.data;
    },
  });

  const suppliersQuery = useQuery<{ suppliers: Supplier[] }>({
    queryKey: ['inventory-suppliers'],
    queryFn: async () => {
      const res = await api.get('/inventory/suppliers');
      return res.data;
    },
  });

  const createOrderMutation = useMutation({
    mutationFn: async (data: {
      itemId: string;
      quantityOrdered: number;
      supplierName: string;
      isPartner?: boolean;
      partnerCampaignId?: string | null;
      notes?: string | null;
    }) => {
      const res = await api.post('/inventory/orders', data);
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['inventory'] });
      queryClient.invalidateQueries({ queryKey: ['inventory-alerts'] });
      queryClient.invalidateQueries({ queryKey: ['inventory-orders'] });
    },
  });

  const updateOrderMutation = useMutation({
    mutationFn: async ({ id, status }: { id: string; status: 'ordered' | 'received' | 'cancelled' }) => {
      const res = await api.patch(`/inventory/orders/${id}`, { status });
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['inventory'] });
      queryClient.invalidateQueries({ queryKey: ['inventory-alerts'] });
      queryClient.invalidateQueries({ queryKey: ['inventory-orders'] });
    },
  });

  return {
    insights: insightsQuery.data?.insights ?? [],
    isLoadingInsights: insightsQuery.isLoading,
    isErrorInsights: insightsQuery.isError,
    orders: ordersQuery.data?.orders ?? [],
    isLoadingOrders: ordersQuery.isLoading,
    suppliers: suppliersQuery.data?.suppliers ?? [],
    isLoadingSuppliers: suppliersQuery.isLoading,
    createOrder: createOrderMutation.mutateAsync,
    isCreatingOrder: createOrderMutation.isPending,
    updateOrder: updateOrderMutation.mutateAsync,
    isUpdatingOrder: updateOrderMutation.isPending,
  };
}
